import { AnswerSchema, CarouselSchema, StepSchema } from '@feedyou/schema'
import { isNotEmptyElement } from '@feedyou/utils'
import { getAllPossibleValuesForSpecifiedPropertiesInMutableObject } from '../../../lib/mutation'

export const forStep = (step: StepSchema): string[] => {
  switch (step.type) {
    case 'message': {
      // There is a slight caveat to stepStorageSummary: if row.storage has no variables
      // interpolated in curly brackets, then the whole row.storage string is treated as a storage
      const stepStorageSummaryTexts = step.storageSummary?.rows
        ?.map(row => [row.title, row.storage])
        .flat()
        .filter(isNotEmptyElement)

      const carouselTexts = step.carousel
        ?.map(getTextsThatSupportInterpolatingVariables.forCarousel)
        .flat()

      const directStepTexts = getAllPossibleValuesForSpecifiedPropertiesInMutableObject(
        step, // I <3 PRETTIER
        ['message']
      )

      return [directStepTexts, stepStorageSummaryTexts, carouselTexts]
        .flat() // I <3 PRETTIER
        .filter(isNotEmptyElement)
    }

    case 'question': {
      const answerTexts = step.answers
        ?.map(getTextsThatSupportInterpolatingVariables.forAnswer)
        .flat()

      const directStepTexts = getAllPossibleValuesForSpecifiedPropertiesInMutableObject(
        step, // I <3 PRETTIER
        [
          'question',
          'skipAnswer',
          'skipFirstAnswer',
          'questionFirst',
          'otherAnswer',
          'otherAnswerQuestion',
          'questionMale',
          'questionFemale'
        ]
      )

      return [directStepTexts, answerTexts].flat().filter(isNotEmptyElement)
    }

    case 'prompt': {
      const directStepTexts = getAllPossibleValuesForSpecifiedPropertiesInMutableObject(
        step, // I <3 PRETTIER
        ['prompt', 'quickReplies', 'promptMale', 'promptFemale']
      ).flat()

      return directStepTexts.flat().filter(isNotEmptyElement)
    }

    case 'switch': {
      const directStepTexts = getAllPossibleValuesForSpecifiedPropertiesInMutableObject(
        step, // I <3 PRETTIER
        ['title']
      ).flat()

      const caseTexts = [step.cases, step.default]
        .flat()
        .filter(isNotEmptyElement)
        .map(stepCase =>
          getAllPossibleValuesForSpecifiedPropertiesInMutableObject(
            stepCase, // I <3 PRETTIER
            ['title']
          )
        )
        .flat()

      return [directStepTexts, caseTexts].flat().filter(isNotEmptyElement)
    }

    case 'email': {
      const directStepTexts = getAllPossibleValuesForSpecifiedPropertiesInMutableObject(
        step, // I <3 PRETTIER
        [
          'subject',
          'recipient',
          'text',
          'ccRecipient',
          'bccRecipient',
          'replyTo',
          'fromName',
          'fromAddress',
          'bodyPrefix',
          'bodyPostfix',
          'body',
          'footer'
        ]
      )

      // There is a slight caveat to stepStorageSummary: if row.storage has no variables
      // interpolated in curly brackets, then the whole row.storage string is treated as a storage
      const stepStorageSummaryTexts = step.storageSummary?.rows
        ?.map(row => [row.title, row.storage])
        .flat()
        .filter(isNotEmptyElement)

      const buttonsText = step.buttons
        ?.map(button => getAllPossibleValuesForSpecifiedPropertiesInMutableObject(button, ['text']))
        .flat()

      return [stepStorageSummaryTexts, buttonsText, directStepTexts]
        .flat()
        .filter(isNotEmptyElement)
    }

    case 'attachment': {
      const directStepTexts = getAllPossibleValuesForSpecifiedPropertiesInMutableObject(
        step, // I <3 PRETTIER
        ['prompt', 'promptMale', 'promptFemale']
      )

      return [directStepTexts].flat().filter(isNotEmptyElement)
    }

    case 'integration': {
      return []
    }
  }

  return []
}

const forCarousel = (carousel?: CarouselSchema) => {
  if (!carousel) {
    return []
  }

  const directTexts = getAllPossibleValuesForSpecifiedPropertiesInMutableObject(
    carousel, // I <3 PRETTIER
    ['title', 'subtitle', 'text']
  )

  const buttonTexts = carousel.buttons
    ?.map(button => {
      return getAllPossibleValuesForSpecifiedPropertiesInMutableObject(
        button, // I <3 PRETTIER
        ['title', 'msg']
      )
    })
    .flat()
    .filter(isNotEmptyElement)

  return [directTexts, buttonTexts].flat().filter(isNotEmptyElement)
}

const forAnswer = (answer: AnswerSchema): string[] => {
  return [
    ...getAllPossibleValuesForSpecifiedPropertiesInMutableObject(answer, ['answer']),
    ...getTextsThatSupportInterpolatingVariables.forCarousel(answer),
    answer.synonyms
  ]
    .flat()
    .filter(isNotEmptyElement)
}

export const getTextsThatSupportInterpolatingVariables = {
  forStep,
  forCarousel,
  forAnswer
}
